// ─── Enums / string unions ────────────────────────────────────────────────────

export type ResponseRole = 'user' | 'assistant' | 'system' | 'developer';

export type ResponseItemStatus =
    | 'in_progress'
    | 'completed'
    | 'failed'
    | 'cancelled'
    | 'queued'
    | 'incomplete';

export type ImageDetail = 'low' | 'high' | 'auto';

export type ResponsesMessageType =
    | 'user_message'
    | 'assistant_message'
    | 'system_message'
    | 'developer_message'
    | 'function_call'
    | 'file_search_call'
    | 'code_interpreter_call'
    | 'web_search_call'
    | 'function_call_output'
    | 'reasoning'
    | 'mcp_list_tools'
    | 'mcp_call'
    | 'mcp_approval_request'
    | 'mcp_approval_response'
    | 'unknown_or_unsupported';

// ─── Pages-specific types ────────────────────────────────────────────────────

export interface FollowUps {
    text: string;
    url: string;
}

export enum CitationType {
    QA = 0,
    Article = 1,
    Web = 2
}

export interface CitationInfo {
    fileId: string;
    fileName: string;
    score: number;
    title: string;
    url?: string;
    content: string;
    followUpQuestions: FollowUps[];
    citationType: CitationType;
}

// ─── Message record (matches theta-assist ResponsesMessageRecord) ─────────────

export interface ResponsesMessageRecord {
    id: string;
    threadId: string;
    messageType: ResponsesMessageType;
    messageStatus: string;
    message: ItemDto | null;
    metadata: Record<string, string>;
    createdAt: string;
}

// ─── Item DTOs ────────────────────────────────────────────────────────────────

export interface MessageItemDto {
    type: 'message';
    id?: string;
    status?: ResponseItemStatus;
    role: ResponseRole;
    content: ContentPartDto[];
}

export interface FunctionToolOutputRequestItemDto {
    type: 'function_call_output';
    call_id: string;
    output: string;
}

export interface FunctionToolCallResponseItemDto {
    type: 'function_call';
    id: string;
    status: ResponseItemStatus;
    call_id: string;
    name: string;
    arguments: string;
    output?: string;
}

export interface FileSearchToolCallResponseItemDto {
    type: 'file_search_call';
    id: string;
    status: string;
    queries: string[];
    results: FileSearchResultItem[] | null;
}

export interface FileSearchResultItem {
    file_id: string;
    filename: string;
    score: number;
    text?: string;
}

export interface ReasoningSummaryTextPartDto {
    type: 'summary_text';
    text: string;
}
export type ReasoningSummaryPartDto = ReasoningSummaryTextPartDto;

export interface ReasoningResponseItemDto {
    type: 'reasoning';
    id: string;
    summary: ReasoningSummaryPartDto[];
    in_progress?: boolean;
}

export interface CodeInterpreterCallResponseItemDto {
    type: 'code_interpreter_call';
    id: string;
    status: ResponseItemStatus;
    code: string;
    container_id: string;
    in_progress?: boolean;
}

export type WebSearchActionDto =
    | { type: 'search'; query?: string }
    | { type: 'open_page'; url: string };

export interface WebSearchCallResponseItemDto {
    type: 'web_search_call';
    id: string;
    status: ResponseItemStatus;
    action?: WebSearchActionDto;
}

export interface McpAvailableToolDto {
    name: string;
    description?: string;
}

export interface McpListToolsResponseItemDto {
    type: 'mcp_list_tools';
    id: string;
    server_label: string;
    server_name?: string | null;
    tools: McpAvailableToolDto[];
}

export interface McpCallResponseItemDto {
    type: 'mcp_call';
    id: string;
    server_label: string;
    server_name?: string | null;
    name: string;
    arguments: string;
    output?: string;
    error?: unknown;
    approval_request_id?: string | null;
}

export interface McpApprovalRequestItemDto {
    type: 'mcp_approval_request';
    id: string;
    server_label: string;
    server_name?: string | null;
    name: string;
    arguments: string;
}

export interface McpApprovalResponseItemDto {
    type: 'mcp_approval_response';
    approval_request_id: string;
    approve: boolean;
}

export interface UnknownOrUnsupportedResponseItemDto {
    type: string;
    original_type?: string;
    raw_json?: string;
}

export type KnownItemDto =
    | MessageItemDto
    | FunctionToolOutputRequestItemDto
    | FunctionToolCallResponseItemDto
    | FileSearchToolCallResponseItemDto
    | ReasoningResponseItemDto
    | CodeInterpreterCallResponseItemDto
    | WebSearchCallResponseItemDto
    | McpListToolsResponseItemDto
    | McpCallResponseItemDto
    | McpApprovalRequestItemDto
    | McpApprovalResponseItemDto;

export type ItemDto = KnownItemDto | UnknownOrUnsupportedResponseItemDto;

// ─── Annotation DTOs ──────────────────────────────────────────────────────────

export interface FileCitationAnnotationDto {
    type: 'file_citation';
    file_id: string;
    filename: string;
    index: number;
}

export interface UrlCitationAnnotationDto {
    type: 'url_citation';
    url: string;
    start_index: number;
    end_index: number;
    title: string;
}

export interface ContainerFileCitationAnnotationDto {
    type: 'container_file_citation';
    container_id: string;
    file_id: string;
    filename: string;
    start_index: number;
    end_index: number;
}

export type AnnotationDto =
    | FileCitationAnnotationDto
    | UrlCitationAnnotationDto
    | ContainerFileCitationAnnotationDto;

// ─── Content part DTOs ────────────────────────────────────────────────────────

export interface InputTextContentPartDto {
    type: 'input_text';
    text: string;
}

export interface InputImageContentPartDto {
    type: 'input_image';
    image_url?: string;
    file_id?: string;
    detail?: ImageDetail;
}

export interface InputFileContentPartDto {
    type: 'input_file';
    file_id: string;
}

export interface OutputTextContentPartDto {
    type: 'output_text';
    text: string;
    annotations: AnnotationDto[];
}

export interface RefusalContentPartDto {
    type: 'refusal';
    refusal: string;
}

export interface ImageFileDetailDto {
    file_id: string;
}

export interface ImageFileContentPartDto {
    type: 'image_file';
    image_file: ImageFileDetailDto;
}

export type ContentPartDto =
    | InputTextContentPartDto
    | InputImageContentPartDto
    | InputFileContentPartDto
    | OutputTextContentPartDto
    | RefusalContentPartDto
    | ImageFileContentPartDto;

// ─── Streaming update events (matches theta-assist StreamingUpdateData) ────────

interface BaseStreamingUpdate {
    sequence_number?: number;
}

/** CUSTOM: Error event from the Pages backend. */
export interface ErrorEvent extends BaseStreamingUpdate {
    type: 'error';
    message?: string;
}

/** API: A response has been created. */
export interface ResponseCreated extends BaseStreamingUpdate {
    type: 'response.created';
}

/** API: The response is now in progress. */
export interface ResponseInProgress extends BaseStreamingUpdate {
    type: 'response.in_progress';
}

/** API: A new item (message or tool call) is added to the output. */
export interface ResponseOutputItemAdded extends BaseStreamingUpdate {
    type: 'response.output_item.added';
    output_index: number;
    item: ItemDto;
}

/** API: File search in progress. */
export interface ResponseFileSearchCallInProgress extends BaseStreamingUpdate {
    type: 'response.file_search_call.in_progress';
    item_id: string;
}

/** API: File search actively searching. */
export interface ResponseFileSearchCallSearching extends BaseStreamingUpdate {
    type: 'response.file_search_call.searching';
    item_id: string;
}

/** API: File search completed. */
export interface ResponseFileSearchCallCompleted extends BaseStreamingUpdate {
    type: 'response.file_search_call.completed';
    item_id: string;
}

/** API: Web search in progress. */
export interface ResponseWebSearchCallInProgress extends BaseStreamingUpdate {
    type: 'response.web_search_call.in_progress';
    item_id: string;
}

/** API: Web search completed. */
export interface ResponseWebSearchCallCompleted extends BaseStreamingUpdate {
    type: 'response.web_search_call.completed';
    item_id: string;
}

/** API: Code interpreter in progress. */
export interface ResponseCodeInterpreterCallInProgress extends BaseStreamingUpdate {
    type: 'response.code_interpreter_call.in_progress';
    item_id: string;
}

/** API: A chunk of code for a code interpreter call. */
export interface ResponseCodeInterpreterCallCodeDelta extends BaseStreamingUpdate {
    type: 'response.code_interpreter_call_code.delta';
    item_id: string;
    delta: string;
}

/** API: Code interpreter completed. */
export interface ResponseCodeInterpreterCallCompleted extends BaseStreamingUpdate {
    type: 'response.code_interpreter_call.completed';
    item_id: string;
}

/** API: A reasoning summary chunk. */
export interface ResponseReasoningSummaryTextDelta extends BaseStreamingUpdate {
    type: 'response.reasoning_summary_text.delta';
    item_id: string;
    delta: string;
}

/** API: An MCP call in progress. */
export interface ResponseMcpCallInProgress extends BaseStreamingUpdate {
    type: 'response.mcp_call.in_progress';
    item_id: string;
}

/** API: An MCP call completed. */
export interface ResponseMcpCallCompleted extends BaseStreamingUpdate {
    type: 'response.mcp_call.completed';
    item_id: string;
}

/** API: An MCP approval request created. */
export interface ResponseMcpApprovalRequestCreated extends BaseStreamingUpdate {
    type: 'response.mcp_approval_request.created';
    item_id: string;
}

/** API: An item in the output array is complete.
 *  Extended with Pages-specific citationsData injected by the backend. */
export interface ResponseOutputItemDone extends BaseStreamingUpdate {
    type: 'response.output_item.done';
    output_index: number;
    item: ItemDto;
    /** Pages extension: resolved CitationInfo array injected for file_search_call items. */
    citationsData?: CitationInfo[];
}

/** API: A new content part added. */
export interface ResponseContentPartAdded extends BaseStreamingUpdate {
    type: 'response.content_part.added';
    item_id: string;
    content_index: number;
}

/** API: A chunk of text streamed. */
export interface ResponseOutputTextDelta extends BaseStreamingUpdate {
    type: 'response.output_text.delta';
    item_id: string;
    output_index: number;
    content_index: number;
    delta: string;
}

/** API: An annotation added to output text. */
export interface ResponseOutputTextAnnotationAdded extends BaseStreamingUpdate {
    type: 'response.output_text.annotation.added';
    item_id: string;
    annotation: AnnotationDto;
}

/** API: A text block fully complete. */
export interface ResponseOutputTextDone extends BaseStreamingUpdate {
    type: 'response.output_text.done';
    item_id: string;
    text: string;
}

/** API: Content part complete. */
export interface ResponseContentPartDone extends BaseStreamingUpdate {
    type: 'response.content_part.done';
    item_id: string;
}

/** API: The entire response is complete. */
export interface ResponseCompleted extends BaseStreamingUpdate {
    type: 'response.completed';
}

/** API: Keep-alive heartbeat. */
export interface Heartbeat {
    type: 'heartbeat';
}

/** API / Pages: Stream finished. */
export interface Done {
    type: 'done';
}

export type StreamingUpdateData =
    | ErrorEvent
    | ResponseCreated
    | ResponseInProgress
    | ResponseOutputItemAdded
    | ResponseFileSearchCallInProgress
    | ResponseFileSearchCallSearching
    | ResponseFileSearchCallCompleted
    | ResponseWebSearchCallInProgress
    | ResponseWebSearchCallCompleted
    | ResponseCodeInterpreterCallInProgress
    | ResponseCodeInterpreterCallCodeDelta
    | ResponseCodeInterpreterCallCompleted
    | ResponseReasoningSummaryTextDelta
    | ResponseMcpCallInProgress
    | ResponseMcpCallCompleted
    | ResponseMcpApprovalRequestCreated
    | ResponseOutputItemDone
    | ResponseContentPartAdded
    | ResponseOutputTextDelta
    | ResponseOutputTextAnnotationAdded
    | ResponseOutputTextDone
    | ResponseContentPartDone
    | ResponseCompleted
    | Heartbeat
    | Done;
